import { Box, Button, Typography, useMediaQuery } from "@mui/material";
import BackgroundLayout from "../../../components/custom-backgrounds/BackgroundLayout";
import {
  FilterPayload,
  EducationalContentGroup,
  EducationalContentItem,
} from "../../../utility/interfaces/educationLibraryInterfaces";
import TrimesterTabs from "../../../features/trimester-tabs/TrimesterTabs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import InFocusContent from "../../../features/learning-in-focus/InFocusContent";
import styles from "./MyLearnings.module.scss";
import {
  CONTENT_STATUS_COMPLETED_ID,
  EDUCATION_CONTENT_SORCE_ID,
  SAVE_CLICK_STATUS_ID,
  SKY_BLUE,
} from "../../../utility/constants/common-constants";
import CategoryOverlay from "../../../features/learning-category-details/CategoryOverlay";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { KeyboardArrowDownSharp } from "@mui/icons-material";
import { useGetLearningLibraryContentApiQuery } from "../../../features/learning-library/services/GetLearningLibraryApiSlice";
import { setLearningContent } from "../../../features/view-learning-content/services/LearningContentDataSlice";
import {
  getContentDuration,
  getPatientRoute,
  handleExternalLinkClick,
} from "../../../utility/functions/common-functions";
import { useLocation, useNavigate } from "react-router-dom";
import { useSaveContentClickMutation } from "../../../features/view-learning-content/services/SaveContentStatusAccessLogAPISlice";
import { useSaveContentStatusMutation } from "../../../features/view-learning-content/services/SaveContentStatusAPISlice";
import FilterOptions from "../../../features/learning-filters/LearningFilter";
import MyLearningCategoryList from "./MyLearningCategoryList";
import DataLoader from "../../../components/custom-data-loader/DataLoader";
import EmptyComponent from "../../../components/v2/custom-empty-error-component/EmptyComponent";
import MyLearningCustomSkeleton from "./MyLearningCustomSkeleton";
import ViewLearningContent from "../../../features/view-learning-content/ViewLearningContent";
import { CHILDLIVINGSTATUSATBIRTH_STATUS_ID_MAPPING } from "./../../../utility/constants/common-constants";
import dayjs from "dayjs";
import TrimesterTabsSkeleton from "../../../features/trimester-tabs/TrimesterTabsSkeleton";

const MyLearnings = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 900px)");
  const isMobileBG = useMediaQuery("(max-width: 600px)");
  const { t } = useTranslation();
  const [selectedCategory] = useState<string | number>(0);
  const [openedCategory, setOpenedCategory] = useState<string | number>("");

  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const categoriesTopRef = useRef<HTMLDivElement | null>(null);

  const isViewLearningContentEnabled = useAppSelector(
    (state) =>
      state.learningContent.navigationInfo?.isViewLearningContentEnabled
  );

  const { livingStatusAtBirthId } = useAppSelector(
    (state) => state.patientOnboardingStatus.loginWorkflowInfo
  );

  const shouldShowLearningWidgets =
    livingStatusAtBirthId ===
      CHILDLIVINGSTATUSATBIRTH_STATUS_ID_MAPPING.living ||
    livingStatusAtBirthId === null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.state]);

  const defaultFilters: FilterPayload = {
    rewardsEligibility: false,
    hideCompleted: false,
    contentType: [],
    trimester: [],
    category: [],
  };

  const [appliedFilters, setAppliedFilters] =
    useState<FilterPayload>(defaultFilters);

  const patientPreferredLang = useAppSelector(
    (state) => state.patientOnboardingStatus.preferredLanguage
  );

  const {
    patientProfileInfo: { patientProfileId, userName: patientUserName },
  } = useAppSelector((state) => state.patientProfileDetails);

  const { data, error, isLoading } = useGetLearningLibraryContentApiQuery({
    patientProfileId: patientProfileId,
    lang: patientPreferredLang,
    body: appliedFilters,
  });

  const [saveContentClick] = useSaveContentClickMutation();
  const [contentStatus] = useSaveContentStatusMutation();

  const filterOptions = useMemo(() => {
    if (!data?.educationlLibrary) return [];

    const seen = new Map<string, string>();

    data?.educationlLibrary?.forEach((item: EducationalContentGroup) => {
      if (!seen.has(item?.activitySubCategoryName)) {
        seen.set(item?.activitySubCategory, item?.activitySubCategoryName);
      }
    });

    return Array.from(seen.entries())
      .filter(([id, name]) => id && name)
      .map(([id, name]) => ({
        labelKey: name,
        value: id,
      }));
  }, [data]);

  const handleClick = (categoryId: string | number) => {
    setFilterOpen(false);
    setScrollPosition(window.scrollY);
    setOpenedCategory(categoryId);
  };

  const learningContent = useAppSelector((state) => state.learningContent);
  useEffect(() => {
    const handlePopState = () => {
      if (isViewLearningContentEnabled) {
        dispatch(
          setLearningContent({
            ...learningContent,
            navigationInfo: {
              isViewLearningContentEnabled: false,
              arrivedFrom: "",
            },
          })
        );
  
        // Prevent user from going back too far
        navigate(location.pathname, { replace: true });
      }
    };
  
    if (isViewLearningContentEnabled) {
      // Only push one fake state — check if we already pushed
      if (window.history.state === null) {
        window.history.pushState({ fake: true }, "", location.pathname);
      }
  
      window.addEventListener("popstate", handlePopState);
    }
  
    return () => {
      if (isViewLearningContentEnabled) {
        window.removeEventListener("popstate", handlePopState);
      }
    };
  }, [isViewLearningContentEnabled, learningContent, dispatch, location.pathname, navigate]);
  
  

  const showContentDetails = async (content: EducationalContentItem) => {
    const publishedDate = dayjs(content.publishDate);
    setFilterOpen(false);
    if (content?.educationalContentType === "URL") {
      if (content?.educationalContentURL) {
        window.open(
          content?.educationalContentURL,
          "_blank",
          "noopener,noreferrer"
        );
        if (content?.activityCompletionStatus !== t("completed")) {
          handleExternalLinkClick(
            content.casePlanMilestoneMappingId,
            CONTENT_STATUS_COMPLETED_ID,
            patientUserName || "Patient",
            patientProfileId,
            patientPreferredLang,
            dispatch,
            contentStatus
          );
        }
      }
    } else {
      dispatch(
        setLearningContent({
          type: content?.educationalContentType,
          category: content?.activitySubCategoryName,
          isNew: content?.isNew,
          videoUrl:
            content.educationalContentSourceId ===
            EDUCATION_CONTENT_SORCE_ID.AGILITY_CMS_ID
              ? content.educationalContentVideoURL
              : content.educationalContentURL ?? "",
          fullHtml: content?.educationalContentBody,
          title: content?.title,
          summarry: content?.description,
          casePlanMilestoneMappingId: content?.casePlanMilestoneMappingId,
          activityCompletionStatus: content?.activityCompletionStatus,
          patientProfileId,
          lang: patientPreferredLang,
          audioUrl: content.educationalContentAudioURL || "",
          activityPoints: content?.rewardPoint,
          thumbnail: content?.thumbnailURL,
          length: getContentDuration(
            content?.length,
            content?.videoDuration
          ),
          educationalContentSourceId: content?.educationalContentSourceId,
          educationalContentIdentifier: content?.educationalContentId.toString(),
          publisherName: content.articleAuthor
            ? `${content.articleAuthor.name}, ${content.articleAuthor.credentials}`
            : "",
          publishedDate:
            `${t("viewLearningContent.published", {
              date: publishedDate.format("MMM D, YYYY"),
            })}` || "",
          navigationInfo: {
            isViewLearningContentEnabled: true,
            arrivedFrom: location.pathname,
          },
        })
      );

      navigate(getPatientRoute(patientProfileId, "/learning"));
    }

    try {
      const body = {
        casePlanMilestoneMappingId: content?.casePlanMilestoneMappingId || "",
        patientProfileId: patientProfileId,
        planId: 1,
        caseId: 1,
        milestoneId: 1,
        contentIdentifier: content?.educationalContentId.toString(),
        contentWatchedDurationInMins: 1,
        action: SAVE_CLICK_STATUS_ID,
        educationalContentSourceId: content?.educationalContentSourceId,
      };

      await saveContentClick({
        body,
        patientPreferredLang,
      }).unwrap();
    } catch (err: any) {
      console.log(err, "saveContentClick");
    }
  };

  const closeFilter = () => {
    setFilterOpen(false);
  };

  const toggleFilter = (open: boolean) => () => {
    setFilterOpen(open);
  };

  const handleApplyFilters = (filters: any) => {
    setAppliedFilters(filters);
    setFilterOpen(false);
    categoriesTopRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <Box overflow={"hidden"}>
      {openedCategory ? (
        <CategoryOverlay
          categoryObject={data.educationlLibrary.find(
            (block: EducationalContentGroup) =>
              block.activitySubCategory === openedCategory
          )}
          filters={appliedFilters}
          onClose={() => {
            setOpenedCategory("");
            requestAnimationFrame(() => {
              window.scrollTo({ top: scrollPosition, behavior: "auto" });
            });
          }}
        />
      ) : isViewLearningContentEnabled ? (
        <Box>
          <ViewLearningContent />
        </Box>
      ) : (
        <BackgroundLayout
          variant={isMobileBG ? "learningImageMobile" : "learningImageDesktop"}
          height={{ xs: "650px", sm: "780px", md: "550px" }}
          backgroundColor={SKY_BLUE}
          showFooter={true}
        >
          <Box className={styles.backgroundImage} 
          sx={{
            ...(patientPreferredLang === "es" && isMobile && { height: "25%" })
          }} />
          <Box
            zIndex={3}
            pt={isMobile ? "3.12rem" : "2.5rem"}
            pb={isMobile ? "4.38rem" : "5rem"}
            px={isMobile ? "1rem" : 0}
            maxWidth={"996px"}
            margin={"auto"}
          >
            <Box width="100%" zIndex={1}>
              <Box display="flex" flexDirection="column">
                <Box
                  display="flex"
                  alignItems={"center"}
                  justifyContent="space-between"
                  mb={isMobile ? "2rem" : "2.5rem"}
                >
                  <Typography variant={isMobile ? "h2" : "h1"}>
                    {t("educationContent.myLearning")}
                  </Typography>

                  <Button
                    ref={anchorRef}
                    variant="outlined"
                    onClick={toggleFilter(true)}
                    className={styles.filterButton}
                    endIcon={
                      <KeyboardArrowDownSharp
                        sx={{
                          color: "black",
                          fontSize: "20px",
                        }}
                      />
                    }
                  >
                    {t("educationContent.filterBy")}
                  </Button>
                </Box>
                <FilterOptions
                  open={filterOpen}
                  anchorEl={anchorRef.current}
                  onClose={closeFilter}
                  onApply={handleApplyFilters}
                  defaultFilters={appliedFilters}
                />
                <DataLoader
                  isLoading={isLoading}
                  error={error}
                  data={data?.educationlLibrary}
                  loadingComponent={<TrimesterTabsSkeleton />}
                  errorComponent={null}
                  emptyComponent={null}
                >
                  {() => (
                    <TrimesterTabs
                      toggleButtonData={filterOptions}
                      value={selectedCategory}
                      onChange={handleClick}
                      isLearningContent={true}
                    />
                  )}
                </DataLoader>
              </Box>
            </Box>
            {shouldShowLearningWidgets && <InFocusContent />}
            <Box ref={categoriesTopRef} />
            <DataLoader
              isLoading={isLoading}
              error={error}
              data={data?.educationlLibrary}
              loadingComponent={<MyLearningCustomSkeleton />}
              errorComponent={
                <EmptyComponent
                  message={t("anErrorOccurred")}
                  textColor="error"
                />
              }
              emptyComponent={<EmptyComponent message={t("noContentFound")} />}
            >
              {(library) => (
                <MyLearningCategoryList
                  educationLibrary={library}
                  onCategoryClick={handleClick}
                  setContentDetails={showContentDetails}
                />
              )}
            </DataLoader>
          </Box>
        </BackgroundLayout>
      )}
    </Box>
  );
};

export default MyLearnings;
